(ns arthur.ui.tracing
  "Tracing layers — footage or a still to draw over — on their own canvas above
  the stage.

  A REFERENCE, NOT OUTPUT. A tracing symbol resolves to a `:trace` op, which the
  raster refuses and only the stage's resolver asks for (see `clip/trace-op`), so
  it cannot reach an export. It is drawn OVER the picture at one opacity rather
  than under it, because the raster clears to an opaque ground. The canvas is the
  part of the work area on screen at screen resolution, not the raster's, so a
  1280px still is not squeezed through a 320px stage on its way to being seen,
  and footage reaching off the stage is seen there as the picture is.

  The op says where the image goes and which of its frames to show, and nothing
  else: a face's footage is registered to the face because it is placed inside
  it, under the head, so there is nothing about faces in here.

  THREE THINGS KEEP IT STEADY, because a still is decoded asynchronously and the
  frame it belongs to is already on screen by the time it arrives: the cache drops
  the stills used longest ago rather than emptying itself, a layer keeps showing
  the still it last showed until its next one has decoded, and the stills a little
  way ahead of the playhead are asked for before they are needed. Each is a
  separate cause of the same symptom — the tracing blinking out for a frame or two
  — and none of them covers the others: the hold is what survives a seek, the
  reading ahead is what keeps playback from being a frame behind for good."
  (:require [arthur.flow.ingest :as ingest]
            [arthur.ui.canvas :as canvas]))

(defonce ^:private state (atom {:canvas nil :urls {} :images {} :order [] :last {}}))

(defn set-canvas! [el] (swap! state assoc :canvas el))

(defn- footage-urls
  "The footage's still URLs, or nil until its manifest has come back."
  [footage-id on-ready]
  (let [u (get-in @state [:urls footage-id])]
    (when (nil? u)
      (swap! state assoc-in [:urls footage-id] :loading)
      (-> (ingest/manifest! footage-id)
          (.then (fn [m]
                   (swap! state assoc-in [:urls footage-id] (:urls m))
                   (on-ready)))
          (.catch (fn [error]
                    (swap! state assoc-in [:urls footage-id] :failed)
                    (js/console.error "arthur: no stills for footage" footage-id error)))))
    (when (vector? u) u)))

(defn url-of
  "The URL of the still showing frame `frame` of `media`, or nil until it is
  known. A footage symbol's frame 0 is the first frame of its range."
  [{:keys [footage image range]} frame on-ready]
  (cond
    image   (str "/blob/" image)
    footage (some-> (footage-urls footage on-ready) (get (+ (first range) frame)))))

(def ^:private ^:const kept
  "Decoded stills held at once. A 1280px still is about 5MB decoded, and
  scrubbing footage with no holds asks for every frame of it."
  48)

(def ^:private ^:const ahead
  "Frames past the playhead whose stills are asked for while the current one is
  drawn. A still cannot be decoded in the animation frame that wants it, so
  playing footage would otherwise always be a frame or two behind itself."
  8)

(defn- decoded
  "`img` if the still has arrived and can be drawn."
  [^js img]
  (when (and (.-complete img) (pos? (.-naturalHeight img))) img))

(defn- touch!
  "Hold `img` under `url` as the most recently used still, dropping the ones used
  longest ago once there are more than `kept`.

  LEAST RECENTLY USED, touched when it arrives and again on every frame it is
  DRAWN, which is what makes it safe without a list of exceptions: the still on
  screen is the newest thing in the cache and cannot be the one dropped — a still
  held for a hundred frames included. Read-and-not-drawn does not touch, because
  a still read only to be warmed was just inserted anyway."
  [url img]
  (swap! state
         (fn [{:keys [images order] :as s}]
           (let [images (assoc images url img)
                 order  (conj (into [] (remove #{url}) order) url)
                 over   (- (count order) kept)]
             (if (pos? over)
               (assoc s :images (apply dissoc images (subvec order 0 over))
                        :order  (subvec order over))
               (assoc s :images images :order order))))))

(defn- image
  "The still at `url`, or nil until it has loaded. Asks for it the first time it
  is wanted; `on-ready` paints again once it is there.

  A still that fails is remembered as having failed, so a broken URL is one
  console line and one request rather than one of each per animation frame."
  [url on-ready]
  (when url
    (let [held (get-in @state [:images url])]
      (cond
        (= :failed held) nil
        held             (decoded held)
        :else            (let [img (js/Image.)]
                           (set! (.-onload img) on-ready)
                           (set! (.-onerror img)
                                 (fn [_]
                                   (swap! state assoc-in [:images url] :failed)
                                   (js/console.error "arthur: a tracing still did not load" url)))
                           (set! (.-src img) url)
                           (touch! url img)
                           nil)))))

(defn- warm!
  "Ask for the stills the next `ahead` frames of footage will want.

  WHILE PLAYING ONLY, and only for a layer whose frame MOVED since it was last
  painted. A scrub asks for a different `ahead` frames at every step, and a held
  layer shows one still for as long as it is held, so reading ahead through
  either would be requests for stills nobody is going to look at."
  [{:keys [media frame]} on-ready]
  (when (:footage media)
    (doseq [p (range (inc frame) (+ frame 1 ahead))]
      (image (url-of media p on-ready) on-ready))))

(defn paint!
  "Draw trace ops `ops`, in draw order, at `opacity`, over `rect` of the work
  area round a stage `stage` — what is on screen, in stage pixels — at `zoom`
  screen pixels to one. `on-ready` is called when a still or a manifest that was
  missing arrives, to paint again."
  [ops {:keys [opacity playing? rect zoom stage]} on-ready]
  (when-let [^js el (:canvas @state)]
    (let [[x0 y0 x1 y1] rect
          cw (js/Math.round (* zoom (- x1 x0))) ch (js/Math.round (* zoom (- y1 y0)))
          _ (when (not= cw (.-width el)) (set! (.-width el) cw))
          _ (when (not= ch (.-height el)) (set! (.-height el) ch))
          _ (canvas/place! el rect stage)
          ctx  (.getContext el "2d")
          was  (:last @state)]
      (.setTransform ctx 1 0 0 1 0 0)
      (.clearRect ctx 0 0 cw ch)
      ;; What each layer is showing, forgotten for the layers that are gone.
      ;; Before the draw, so switching them all off forgets all of them.
      (swap! state update :last select-keys (map :node ops))
      (set! (.-globalAlpha ctx) opacity)
      (doseq [{:keys [node frame m] [w h] :size :as op} ops]
        ;; THE STILL IT HAS, not nothing. A frame whose still is still decoding
        ;; keeps the one before it rather than blinking off.
        (let [want (url-of (:media op) frame on-ready)
              [img url] (or (when-let [i (image want on-ready)] [i want])
                            (let [url (get-in was [node :url])]
                              (when-let [i (image url on-ready)] [i url])))]
          (when img
            (touch! url img)
            (swap! state assoc-in [:last node] {:url url :frame frame})
            (.setTransform ctx
                           (* zoom (aget m 0)) (* zoom (aget m 1))
                           (* zoom (aget m 2)) (* zoom (aget m 3))
                           (* zoom (- (aget m 4) x0)) (* zoom (- (aget m 5) y0)))
            (.drawImage ctx img 0 0 w h))
          (when (and playing? (not= frame (get-in was [node :frame])))
            (warm! op on-ready)))))))
